/**
 * Playground tab — simulate conversation against the Atendente IA prompt.
 *
 * MVP single-LLM-call: usa o systemPrompt derivado dos blocks salvos +
 * variáveis substituídas + chat client-side. Quando OpenSpec #3 chegar,
 * ganha trace per-agent (Field Extractor → CRM Manager → Rules Engine → SDR).
 *
 * Custos cada simulate gera entrada em ai-usage-events com agent='playground',
 * visível na aba Uso (segregado dos custos de produção).
 */
import React, { useState } from 'react';
import {
  runPlaygroundTurn,
  PlaygroundRunResult,
} from '../../services/iaConsoleSdr';

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  metrics?: {
    modelId: string;
    providerId: string;
    inputTokens: number;
    outputTokens: number;
    latencyMs: number;
  };
}

interface PlaygroundTabProps {
  canRun: boolean;
}

const DEFAULT_VARIABLES = {
  lead_name: 'João Silva',
  phone: '+55 11 98888-7777',
  tipo: 'PJ',
  cidade: 'São Paulo',
};

const PlaygroundTab: React.FC<PlaygroundTabProps> = ({ canRun }) => {
  const [variables, setVariables] = useState<Record<string, string>>(DEFAULT_VARIABLES);
  const [tier, setTier] = useState<'fast' | 'standard' | 'quality'>('standard');
  const [conversation, setConversation] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<PlaygroundRunResult | null>(null);
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  /**
   * Acumula os campos extraídos pelo Field Extractor turn-a-turn. Enviado em
   * cada simulate para que o CRM Manager (e o próprio Extractor) saiba o que
   * já foi coletado — sinal crítico pra detectar `proposal_ready` quando todos
   * os obrigatórios estiverem preenchidos.
   */
  const [accumulatedKnownFields, setAccumulatedKnownFields] = useState<Record<string, unknown>>({});

  const lastMetrics = lastResult
    ? {
        modelId: lastResult.modelId,
        providerId: lastResult.providerId,
        inputTokens: lastResult.inputTokens,
        outputTokens: lastResult.outputTokens,
        latencyMs: lastResult.latencyMs,
      }
    : null;

  async function send() {
    if (!draft.trim() || sending || !canRun) return;
    setError(null);
    const userTurn: ChatTurn = { role: 'user', content: draft.trim() };
    const history = [...conversation, userTurn];
    setConversation(history);
    setDraft('');
    setSending(true);
    try {
      const result = await runPlaygroundTurn({
        inboundText: userTurn.content,
        conversationHistory: conversation.map((t) => ({ role: t.role, content: t.content })),
        variables,
        tier,
        knownFields: accumulatedKnownFields,
      });
      setLastResult(result);
      // Merge campos extraídos neste turno no acumulado — turn N+1 verá tudo.
      if (result.fieldExtraction?.extracted) {
        setAccumulatedKnownFields((prev) => ({ ...prev, ...result.fieldExtraction!.extracted }));
      }
      setConversation((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.text,
          metrics: {
            modelId: result.modelId,
            providerId: result.providerId,
            inputTokens: result.inputTokens,
            outputTokens: result.outputTokens,
            latencyMs: result.latencyMs,
          },
        },
      ]);
    } catch (err: any) {
      const apiError = err?.response?.data || err;
      setError(apiError?.message || apiError?.error || 'Erro ao simular turno');
      // Roll back user turn so user can retry
      setConversation((prev) => prev.slice(0, -1));
      setDraft(userTurn.content);
    } finally {
      setSending(false);
    }
  }

  function reset() {
    setConversation([]);
    setLastResult(null);
    setError(null);
    setDraft('');
    setAccumulatedKnownFields({});
  }

  if (!canRun) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted, #6b7280)' }}>
        Apenas owner/admin/gerente/super_admin podem usar o Playground.
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr 300px', gap: 14, padding: 18 }}>
      {/* Left: Variables + tier + prompt preview */}
      <aside style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14 }}>
          <header style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#374151' }}>
            Variáveis do teste
          </header>
          {Object.entries(variables).map(([k, v]) => (
            <div key={k} style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <code style={{ fontSize: 11, color: '#4338ca', fontFamily: 'JetBrains Mono, monospace' }}>{`{{${k}}}`}</code>
              <input
                type="text"
                value={v}
                onChange={(e) => setVariables((p) => ({ ...p, [k]: e.target.value }))}
                style={{ padding: '5px 9px', fontSize: 12, border: '1px solid var(--border, #e5e7eb)', borderRadius: 6 }}
              />
            </div>
          ))}
        </div>

        <div style={{ background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14 }}>
          <header style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#374151' }}>
            Tier do modelo
          </header>
          <div style={{ display: 'flex', gap: 4, padding: 4, background: '#f3f4f6', borderRadius: 8 }}>
            {(['fast', 'standard', 'quality'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTier(t)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: 11.5,
                  fontWeight: 600,
                  background: tier === t ? '#fff' : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  boxShadow: tier === t ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {t === 'fast' ? 'Rápido' : t === 'standard' ? 'Padrão' : 'Qualidade'}
              </button>
            ))}
          </div>
        </div>

        {lastResult && (
          <div style={{ background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14 }}>
            <button
              type="button"
              onClick={() => setShowSystemPrompt((s) => !s)}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                fontSize: 12,
                fontWeight: 700,
                color: '#374151',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ transform: showSystemPrompt ? 'rotate(90deg)' : 'rotate(0deg)', transition: '.15s' }}>▶</span>
              Prompt resolvido ({lastResult.resolvedSystemPrompt.length} chars)
            </button>
            {showSystemPrompt && (
              <pre
                style={{
                  marginTop: 8,
                  padding: 10,
                  background: '#f9fafb',
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.5,
                  maxHeight: 220,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              >
                {lastResult.resolvedSystemPrompt}
              </pre>
            )}
          </div>
        )}
      </aside>

      {/* Center: chat */}
      <div style={{ display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, overflow: 'hidden', minHeight: 500 }}>
        <header style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #e5e7eb)', display: 'flex', alignItems: 'center', gap: 10, background: '#f9fafb' }}>
          <span style={{ width: 32, height: 32, borderRadius: '50%', background: '#10b981', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
            {(variables.lead_name || 'L').slice(0, 1).toUpperCase()}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{variables.lead_name || 'Lead simulado'}</div>
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>{variables.phone} · simulação · {tier}</div>
          </div>
          <button
            type="button"
            onClick={reset}
            disabled={conversation.length === 0}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              background: 'transparent',
              border: '1px solid var(--border, #e5e7eb)',
              borderRadius: 6,
              cursor: conversation.length === 0 ? 'not-allowed' : 'pointer',
              opacity: conversation.length === 0 ? 0.4 : 1,
            }}
          >
            Reiniciar
          </button>
        </header>

        <div style={{ flex: 1, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10, background: '#FBFBFD', overflowY: 'auto', minHeight: 0 }}>
          {conversation.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, marginTop: 60 }}>
              Digite uma mensagem como se fosse o lead.<br />
              <span style={{ fontSize: 11 }}>Ex.: "Quanto custa o curso?", "Quero saber mais", "Obrigado"</span>
            </div>
          )}
          {conversation.map((turn, i) => (
            <div key={i}>
              {turn.role === 'user' ? (
                <div
                  style={{
                    alignSelf: 'flex-end',
                    maxWidth: '70%',
                    marginLeft: 'auto',
                    background: '#DCF8C6',
                    border: '1px solid #CDEFB7',
                    padding: '7px 11px',
                    borderRadius: '10px 10px 2px 10px',
                    fontSize: 13,
                    lineHeight: 1.45,
                    color: '#0F172A',
                  }}
                >
                  {turn.content}
                </div>
              ) : (
                <div>
                  <div
                    style={{
                      alignSelf: 'flex-start',
                      maxWidth: '70%',
                      background: '#fff',
                      border: '1px solid var(--border, #e5e7eb)',
                      padding: '7px 11px',
                      borderRadius: '10px 10px 10px 2px',
                      fontSize: 13,
                      lineHeight: 1.45,
                      color: '#0F172A',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {turn.content}
                  </div>
                  {turn.metrics && (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 10.5,
                        color: '#6b7280',
                        fontFamily: 'JetBrains Mono, monospace',
                      }}
                    >
                      ✦ {turn.metrics.providerId}/{turn.metrics.modelId} · {turn.metrics.inputTokens}+{turn.metrics.outputTokens}t · {turn.metrics.latencyMs}ms
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div style={{ alignSelf: 'flex-start', fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
              ✦ pensando…
            </div>
          )}
          {error && (
            <div style={{ alignSelf: 'flex-start', padding: '8px 11px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 12, maxWidth: '92%' }}>
              ✗ {error}
            </div>
          )}
        </div>

        <footer style={{ padding: '12px 14px', borderTop: '1px solid var(--border, #e5e7eb)', display: 'flex', gap: 8, background: '#fff' }}>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder="Digite como o lead…"
            disabled={sending}
            style={{
              flex: 1,
              padding: '9px 12px',
              fontSize: 13,
              border: '1px solid var(--border, #e5e7eb)',
              borderRadius: 8,
            }}
          />
          <button
            type="button"
            onClick={send}
            disabled={!draft.trim() || sending}
            style={{
              padding: '9px 18px',
              fontSize: 13,
              fontWeight: 600,
              background: '#4338ca',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: !draft.trim() || sending ? 'not-allowed' : 'pointer',
              opacity: !draft.trim() || sending ? 0.5 : 1,
            }}
          >
            {sending ? '…' : 'Enviar'}
          </button>
        </footer>
      </div>

      {/* Right: inspector */}
      <aside style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14 }}>
          <header style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: '#374151' }}>
            Último turno
          </header>
          {lastMetrics ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Stat label="Latência" value={`${lastMetrics.latencyMs}ms`} />
              <Stat label="Tokens" value={`${lastMetrics.inputTokens + lastMetrics.outputTokens}`} />
              <Stat label="Input" value={`${lastMetrics.inputTokens}t`} />
              <Stat label="Output" value={`${lastMetrics.outputTokens}t`} />
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: 12 }}>
              Envie a primeira mensagem.
            </div>
          )}
        </div>

        {lastMetrics && (
          <div style={{ background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14 }}>
            <header style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#374151' }}>Provider</header>
            <div style={{ fontSize: 11.5, color: '#4b5563', fontFamily: 'JetBrains Mono, monospace' }}>
              {lastMetrics.providerId}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              modelId: <code>{lastMetrics.modelId}</code>
            </div>
          </div>
        )}

        {/* Field Extractor preview — OpenSpec #2 (shadow mode quando flag on).
            Mostra o ACUMULADO de tudo capturado na conversa, não apenas o
            delta deste turno. Campos novos do turno atual ficam destacados. */}
        {lastResult?.fieldExtraction && (() => {
          const turnDelta = lastResult.fieldExtraction.extracted || {};
          const turnDeltaKeys = new Set(Object.keys(turnDelta));
          const accumulatedEntries = Object.entries(accumulatedKnownFields);
          return (
            <div style={{ background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14 }}>
              <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Field Extractor</span>
                <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                  shadow
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#6b7280' }}>
                  {accumulatedEntries.length} campo{accumulatedEntries.length === 1 ? '' : 's'} ·
                  {' '}conf {(lastResult.fieldExtraction.confidence * 100).toFixed(0)}%
                </span>
              </header>
              {accumulatedEntries.length === 0 ? (
                <div style={{ fontSize: 11.5, color: '#9ca3af', padding: '6px 0' }}>
                  Nenhum campo coletado ainda.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {accumulatedEntries.map(([k, v]) => {
                    const isNew = turnDeltaKeys.has(k);
                    return (
                      <div
                        key={k}
                        style={{
                          display: 'flex',
                          gap: 6,
                          fontSize: 11.5,
                          padding: isNew ? '3px 6px' : 0,
                          background: isNew ? '#dcfce7' : 'transparent',
                          borderRadius: 4,
                          alignItems: 'center',
                        }}
                      >
                        {isNew && (
                          <span style={{ color: '#16a34a', fontSize: 9, fontWeight: 700 }} title="Novo neste turno">
                            NEW
                          </span>
                        )}
                        <code style={{ color: '#4338ca', fontFamily: 'JetBrains Mono, monospace' }}>{k}</code>
                        <span style={{ color: '#6b7280' }}>=</span>
                        <span style={{ color: '#0f172a', fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all' }}>
                          {v === null || v === undefined ? 'null' : String(v)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {turnDeltaKeys.size === 0 && accumulatedEntries.length > 0 && (
                <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 6, fontStyle: 'italic' }}>
                  Nada novo neste turno.
                </div>
              )}
            {lastResult.fieldExtraction.refusals.length > 0 && (
              <div style={{ marginTop: 8, padding: '6px 8px', background: '#fee2e2', borderRadius: 6 }}>
                <div style={{ fontSize: 10.5, color: '#991b1b', fontWeight: 600, marginBottom: 4 }}>
                  Recusas
                </div>
                {lastResult.fieldExtraction.refusals.map((r, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#7f1d1d' }}>
                    <code>{r.key}</code>{r.reason ? ` — ${r.reason}` : ''}
                  </div>
                ))}
              </div>
            )}
              <div style={{ marginTop: 8, fontSize: 10, color: '#9ca3af', fontFamily: 'JetBrains Mono, monospace' }}>
                {lastResult.fieldExtraction.providerId}/{lastResult.fieldExtraction.modelId} ·
                {' '}{lastResult.fieldExtraction.latencyMs}ms · {lastResult.fieldExtraction.inputTokens + lastResult.fieldExtraction.outputTokens}t
              </div>
            </div>
          );
        })()}

        {/* CRM Manager — handoff signal — OpenSpec #3 shadow */}
        {lastResult?.crmManagerClassification && (
          <div style={{ background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14 }}>
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Handoff Status</span>
              <span style={{ background: '#fee2e2', color: '#991b1b', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                shadow
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#6b7280' }}>
                conf {(lastResult.crmManagerClassification.handoffConfidence * 100).toFixed(0)}%
              </span>
            </header>
            <HandoffBadge
              signal={lastResult.crmManagerClassification.handoffSignal}
              remarketingMonths={lastResult.crmManagerClassification.remarketingMonths}
              pendingFutureInterest={lastResult.crmManagerClassification.pendingFutureInterest}
            />
            {lastResult.crmManagerClassification.reason && (
              <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
                {lastResult.crmManagerClassification.reason}
              </div>
            )}
          </div>
        )}

        {/* CRM Manager — funnel stage — OpenSpec #3 shadow */}
        {lastResult?.crmManagerClassification && (
          <div style={{ background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14 }}>
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Funil Status</span>
              <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                shadow
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#6b7280' }}>
                conf {(lastResult.crmManagerClassification.funnelConfidence * 100).toFixed(0)}%
              </span>
            </header>
            <FunnelBadge stage={lastResult.crmManagerClassification.funnelStage} />
            <div style={{ marginTop: 8, fontSize: 10, color: '#9ca3af', fontFamily: 'JetBrains Mono, monospace' }}>
              {lastResult.crmManagerClassification.providerId}/{lastResult.crmManagerClassification.modelId} ·
              {' '}{lastResult.crmManagerClassification.latencyMs}ms ·
              {' '}{lastResult.crmManagerClassification.inputTokens + lastResult.crmManagerClassification.outputTokens}t
            </div>
          </div>
        )}

        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 14, color: '#92400e', fontSize: 11.5, lineHeight: 1.5 }}>
          <b>Modo MVP.</b> Esta simulação chama um único LLM (Atendente IA).
          Quando o refactor multi-agente entrar (OpenSpec #3), o painel mostra
          trace per-agent (Field Extractor → CRM Manager → Rules Engine → SDR
          Conversation) com tokens + latência por etapa. Os custos atuais
          aparecem na aba Uso com <code>agent=playground</code>.
        </div>
      </aside>
    </div>
  );
};

type HandoffSignalUi =
  | 'toxic'
  | 'opt_out'
  | 'low_intent'
  | 'request_human'
  | 'proposal_ready'
  | 'remarketing'
  | null;

function HandoffBadge({
  signal,
  remarketingMonths,
  pendingFutureInterest,
}: {
  signal: HandoffSignalUi;
  remarketingMonths?: number;
  pendingFutureInterest?: boolean;
}) {
  if (!signal) {
    // Caso opt-in raro (ADR-0013 revisado): a org configurou via SDR custom
    // que quer coletar campos antes de aceitar adiamento. Default global é
    // disparar `remarketing` direto. Quando esta flag aparece sem o sinal,
    // significa que o tenant pediu coletar primeiro.
    if (pendingFutureInterest) {
      return (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            background: '#fef9c3',
            color: '#854d0e',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 12,
            border: '1px dashed #facc15',
          }}
          title="SDR custom da org pediu coletar campos antes de aceitar adiamento. Default global dispara 'remarketing' direto."
        >
          <span>⏳</span>
          <span>Interesse futuro · coletando antes (opt-in)</span>
        </div>
      );
    }
    return (
      <div style={{ fontSize: 11.5, color: '#059669' }}>
        ✓ Nenhum sinal de handoff — conversa segue na IA.
      </div>
    );
  }
  const map: Record<Exclude<HandoffSignalUi, null>, { label: string; bg: string; fg: string; emoji: string }> = {
    toxic: { label: 'Tóxico', bg: '#fef2f2', fg: '#991b1b', emoji: '⚠️' },
    opt_out: { label: 'Opt-out', bg: '#f0fdf4', fg: '#166534', emoji: '🚫' },
    low_intent: { label: 'Sem Interesse', bg: '#f8fafc', fg: '#475569', emoji: '💤' },
    request_human: { label: 'Pediu Humano', bg: '#fef3c7', fg: '#92400e', emoji: '👤' },
    proposal_ready: { label: 'Pronto p/ Proposta', bg: '#dcfce7', fg: '#166534', emoji: '🎯' },
    remarketing: {
      label:
        typeof remarketingMonths === 'number'
          ? `Remarketing em ${remarketingMonths} ${remarketingMonths === 1 ? 'mês' : 'meses'}`
          : 'Remarketing',
      bg: '#fef3c7',
      fg: '#854d0e',
      emoji: '📅',
    },
  };
  const cfg = map[signal];
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        background: cfg.bg,
        color: cfg.fg,
        borderRadius: 6,
        fontWeight: 700,
        fontSize: 12.5,
      }}
    >
      <span>{cfg.emoji}</span>
      <span>{cfg.label}</span>
    </div>
  );
}

function FunnelBadge({ stage }: { stage: 'inicio' | 'em_negociacao' | 'coletando_dados' }) {
  const map: Record<string, { label: string; bg: string; fg: string; emoji: string; description: string }> = {
    inicio: {
      label: 'Início',
      bg: '#eef2ff',
      fg: '#4338ca',
      emoji: '🌱',
      description: 'IA descobrindo intenção, curso, pergunta, valor.',
    },
    em_negociacao: {
      label: 'Em negociação',
      bg: '#ede9fe',
      fg: '#6d28d9',
      emoji: '💬',
      description: 'Lead pedindo descontos, aprofundando conversa.',
    },
    coletando_dados: {
      label: 'Coletando dados',
      bg: '#dcfce7',
      fg: '#166534',
      emoji: '📋',
      description: 'IA coletando campos para proposta. Handoff humano segue após.',
    },
  };
  const cfg = map[stage];
  return (
    <>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          background: cfg.bg,
          color: cfg.fg,
          borderRadius: 6,
          fontWeight: 700,
          fontSize: 12.5,
        }}
      >
        <span>{cfg.emoji}</span>
        <span>{cfg.label}</span>
      </div>
      <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
        {cfg.description}
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: '8px 10px', background: '#f9fafb', borderRadius: 8 }}>
      <div style={{ fontSize: 10.5, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', fontFamily: 'JetBrains Mono, monospace' }}>{value}</div>
    </div>
  );
}

export default PlaygroundTab;
